import * as React from 'react';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import { MousePointer2, RotateCcw, X, Check, Loader2 } from 'lucide-react';
import { formatClassName } from '../constants/detectionClasses';
import { ShapeMetricBar } from './ui/shape-metric-bar';
import { API_BASE_URL } from '../config/api';

interface Detection {
  class_id: number;
  class_name: string;
  confidence: number;
  bbox: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  size?: {
    width_px: number;
    height_px: number;
    diameter_px: number;
    diameter_um: number;
  };
}

interface SizeStatistics {
  mean_um: number | null;
  median_um: number | null;
  mode_um: number | null;
  std_um: number | null;
  min_um: number | null;
  max_um: number | null;
  count: number;
  shape_metrics?: {
    roundness: number;
    convexity: number;
    aspect_ratio: number;
    solidity: number;
  };
  histogram?: string;
}

interface InferenceResult {
  filename: string;
  image_size: { width: number; height: number };
  results: {
    total_detections: number;
    class_counts: Record<string, number>;
    detections: Detection[];
    statistics?: {
      overall: SizeStatistics;
      by_class: Record<string, SizeStatistics>;
      all_sizes_um: number[];
    };
    histogram?: string;
  };
  annotated_image?: string;
  base_image?: string;
}

interface Props {
  filename: string;
  result: InferenceResult;
  classColors: Record<string, string>;
  classBadgeVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'>;
  pixelToUm: number;
  confThreshold: number;
  onExclusionsChange?: (excludedIndices: Set<number>, detections: Detection[]) => void;
  onApplyExclusions?: (excludedIndices: Set<number>, activeDetections: Detection[]) => void;
  onResetExclusions?: () => void;
  onHistogramsRegenerated?: (histograms: Record<string, string>) => void;
  sizeFilter?: { min: number; max: number };  // Live filter for counts/display
  sizeFilterApplied?: { min: number; max: number };  // Applied filter for histogram regeneration
}

const DetectionResultCard = React.forwardRef<HTMLDivElement, Props>(({
  filename,
  result,
  classColors,
  classBadgeVariants,
  pixelToUm,
  confThreshold,
  onExclusionsChange,
  onApplyExclusions,
  onResetExclusions,
  onHistogramsRegenerated,
  sizeFilter,
  sizeFilterApplied
}, ref) => {
  const [selectedClasses, setSelectedClasses] = React.useState<Set<string>>(
    () => new Set(Object.keys(result.results.class_counts))
  );
  const [expandedClasses, setExpandedClasses] = React.useState<Set<string>>(new Set());
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [baseImage, setBaseImage] = React.useState<HTMLImageElement | null>(null);

  // Exclusion mode state
  const [isExclusionMode, setIsExclusionMode] = React.useState(false);
  const [excludedIndices, setExcludedIndices] = React.useState<Set<number>>(new Set());
  const [appliedExcludedIndices, setAppliedExcludedIndices] = React.useState<Set<number>>(new Set()); // Track what's been applied
  const [hoveredDetectionIndex, setHoveredDetectionIndex] = React.useState<number | null>(null);

  // Regenerated histograms for classes with exclusions
  const [regeneratedHistograms, setRegeneratedHistograms] = React.useState<Record<string, string>>({});
  const [isRegeneratingHistograms, setIsRegeneratingHistograms] = React.useState(false);

  const statsByClass = result.results.statistics?.by_class;

  // Filter detections based on selected classes (with original index preserved)
  const filteredDetectionsWithIndex = result.results.detections
    .map((det, index) => ({ det, originalIndex: index }))
    .filter(({ det }) => selectedClasses.has(det.class_name));

  const filteredDetections = filteredDetectionsWithIndex.map(({ det }) => det);

  // Helper to check if detection passes size filter (for counts/stats)
  const detectionPassesSizeFilter = (det: Detection): boolean => {
    if (!sizeFilter) return true;
    if (!det.size?.diameter_um) return true;
    return det.size.diameter_um >= sizeFilter.min && det.size.diameter_um <= sizeFilter.max;
  };

  // Active detections = filtered - excluded - outside size filter
  const activeDetections = filteredDetectionsWithIndex.filter(
    ({ det, originalIndex }) => !excludedIndices.has(originalIndex) && detectionPassesSizeFilter(det)
  );

  // Count detections filtered out by size (not excluded)
  const sizeFilteredOutCount = sizeFilter ? filteredDetectionsWithIndex.filter(
    ({ det, originalIndex }) => !excludedIndices.has(originalIndex) && !detectionPassesSizeFilter(det)
  ).length : 0;

  const filteredCount = filteredDetections.length;
  const activeCount = activeDetections.length;
  const excludedCount = excludedIndices.size;

  // Check if there are unapplied exclusion changes
  const hasUnappliedChanges = React.useMemo(() => {
    if (excludedIndices.size !== appliedExcludedIndices.size) return true;
    const excludedArray = Array.from(excludedIndices);
    for (let i = 0; i < excludedArray.length; i++) {
      if (!appliedExcludedIndices.has(excludedArray[i])) return true;
    }
    return false;
  }, [excludedIndices, appliedExcludedIndices]);

  // Compute active counts per class (accounting for exclusions)
  const activeCountsByClass = React.useMemo(() => {
    const counts: Record<string, number> = {};
    activeDetections.forEach(({ det }) => {
      counts[det.class_name] = (counts[det.class_name] || 0) + 1;
    });
    return counts;
  }, [activeDetections]);

  // Compute active stats per class (recalculate when exclusions change)
  const activeStatsByClass = React.useMemo(() => {
    const stats: Record<string, {
      count: number;
      mean_um: number | null;
      median_um: number | null;
      std_um: number | null;
      min_um: number | null;
      max_um: number | null;
    }> = {};

    // Group active detections by class
    const sizesByClass: Record<string, number[]> = {};
    activeDetections.forEach(({ det }) => {
      if (det.size?.diameter_um) {
        if (!sizesByClass[det.class_name]) {
          sizesByClass[det.class_name] = [];
        }
        sizesByClass[det.class_name].push(det.size.diameter_um);
      }
    });

    // Calculate stats for each class
    Object.entries(activeCountsByClass).forEach(([className, count]) => {
      const sizes = sizesByClass[className] || [];
      let sizeStats = {
        mean_um: null as number | null,
        median_um: null as number | null,
        std_um: null as number | null,
        min_um: null as number | null,
        max_um: null as number | null,
      };

      if (sizes.length > 0) {
        const sorted = [...sizes].sort((a, b) => a - b);
        const mean = sizes.reduce((a, b) => a + b, 0) / sizes.length;
        const median = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)];
        const variance = sizes.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / sizes.length;
        const std = Math.sqrt(variance);

        sizeStats = {
          mean_um: mean,
          median_um: median,
          std_um: std,
          min_um: sorted[0],
          max_um: sorted[sorted.length - 1],
        };
      }

      stats[className] = { count, ...sizeStats };
    });

    return stats;
  }, [activeDetections, activeCountsByClass]);

  // Compute sizes by class for active detections (used for histogram regeneration)
  const activeSizesByClass = React.useMemo(() => {
    const sizesByClass: Record<string, number[]> = {};
    activeDetections.forEach(({ det }) => {
      if (det.size?.diameter_um) {
        if (!sizesByClass[det.class_name]) {
          sizesByClass[det.class_name] = [];
        }
        sizesByClass[det.class_name].push(det.size.diameter_um);
      }
    });
    return sizesByClass;
  }, [activeDetections]);

  // Determine which classes need histogram regeneration (original count != active count due to exclusions OR size filter)
  const classesNeedingHistogramRegen = React.useMemo(() => {
    const affected = new Set<string>();
    Object.entries(result.results.class_counts).forEach(([className, originalCount]) => {
      const activeCount = activeCountsByClass[className] || 0;
      if (activeCount !== originalCount) {
        affected.add(className);
      }
    });
    return affected;
  }, [result.results.class_counts, activeCountsByClass]);

  // Regenerate histograms for classes affected by filtering
  const regenerateAffectedHistograms = async () => {
    if (classesNeedingHistogramRegen.size === 0) return;

    setIsRegeneratingHistograms(true);
    const newHistograms: Record<string, string> = {};

    const affectedClasses = Array.from(classesNeedingHistogramRegen);
    for (const className of affectedClasses) {
      const sizes = activeSizesByClass[className] || [];
      if (sizes.length === 0) continue;

      try {
        const response = await fetch(`${API_BASE_URL}/inference/regenerate-histogram`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sizes,
            title: `${formatClassName(className)} Size Distribution (n=${sizes.length})`,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          newHistograms[className] = data.histogram;
        }
      } catch (error) {
        console.error(`Failed to regenerate histogram for ${className}:`, error);
      }
    }

    setRegeneratedHistograms(prev => {
      const updated = { ...prev, ...newHistograms };
      // Notify parent of all current regenerated histograms
      if (onHistogramsRegenerated) {
        onHistogramsRegenerated(updated);
      }
      return updated;
    });
    setIsRegeneratingHistograms(false);
  };

  // Regenerate histograms when applied size filter changes (with debounce to prevent infinite loops)
  const prevSizeFilterAppliedRef = React.useRef(sizeFilterApplied);
  const histogramRegenTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    const prevFilter = prevSizeFilterAppliedRef.current;
    const currFilter = sizeFilterApplied;

    // Check if applied size filter actually changed
    const filterChanged = (
      (prevFilter === undefined && currFilter !== undefined) ||
      (prevFilter !== undefined && currFilter === undefined) ||
      (prevFilter && currFilter && (prevFilter.min !== currFilter.min || prevFilter.max !== currFilter.max))
    );

    prevSizeFilterAppliedRef.current = sizeFilterApplied;

    // Clear any pending histogram regeneration
    if (histogramRegenTimeoutRef.current) {
      clearTimeout(histogramRegenTimeoutRef.current);
    }

    // If filter changed and there are classes needing regeneration, trigger it after debounce
    if (filterChanged && classesNeedingHistogramRegen.size > 0) {
      // Debounce histogram regeneration by 300ms to prevent infinite loops
      histogramRegenTimeoutRef.current = setTimeout(() => {
        regenerateAffectedHistograms();
      }, 300);
    }

    // Cleanup timeout on unmount
    return () => {
      if (histogramRegenTimeoutRef.current) {
        clearTimeout(histogramRegenTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeFilterApplied?.min, sizeFilterApplied?.max]);

  // Load base image
  React.useEffect(() => {
    if (!result.base_image) return;
    const img = new Image();
    img.onload = () => setBaseImage(img);
    img.src = `data:image/png;base64,${result.base_image}`;
  }, [result.base_image]);

  // Helper to check if detection passes size filter
  const passesSizeFilter = (det: Detection): boolean => {
    if (!sizeFilter) return true;
    if (!det.size?.diameter_um) return true; // Include detections without size data
    return det.size.diameter_um >= sizeFilter.min && det.size.diameter_um <= sizeFilter.max;
  };

  // Draw canvas when selection, exclusions, size filter, or image changes
  React.useEffect(() => {
    if (!canvasRef.current || !baseImage) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match image
    canvas.width = result.image_size.width;
    canvas.height = result.image_size.height;

    // Draw base image (no annotations)
    ctx.drawImage(baseImage, 0, 0, result.image_size.width, result.image_size.height);

    // Draw detections with exclusion and size filter styling
    filteredDetectionsWithIndex.forEach(({ det, originalIndex }) => {
      const { x1, y1, x2, y2 } = det.bbox;
      const color = classColors[det.class_name] || '#888';
      const isExcluded = excludedIndices.has(originalIndex);
      const isHovered = hoveredDetectionIndex === originalIndex;
      const isOutsideSizeFilter = !passesSizeFilter(det);

      if (isExcluded) {
        // Draw excluded detection with X overlay
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        ctx.setLineDash([]);

        // Draw X across the bbox
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.moveTo(x2, y1);
        ctx.lineTo(x1, y2);
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Semi-transparent overlay
        ctx.fillStyle = 'rgba(255, 0, 0, 0.15)';
        ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      } else if (isOutsideSizeFilter) {
        // Draw size-filtered detection with dimmed/gray style
        ctx.strokeStyle = '#888888';
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 2]);
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        ctx.setLineDash([]);

        // Semi-transparent gray overlay
        ctx.fillStyle = 'rgba(128, 128, 128, 0.3)';
        ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      } else {
        // Normal detection (passes all filters)
        ctx.strokeStyle = color;
        ctx.lineWidth = isHovered && isExclusionMode ? 3 : 2;
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

        // Highlight on hover in exclusion mode
        if (isHovered && isExclusionMode) {
          ctx.fillStyle = 'rgba(255, 255, 0, 0.2)';
          ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
        }
      }
    });

    // Draw exclusion mode indicator
    if (isExclusionMode) {
      ctx.fillStyle = 'rgba(255, 100, 100, 0.9)';
      ctx.fillRect(10, 10, 150, 30);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('EXCLUSION MODE', 20, 30);
    }

    // Draw size filter indicator
    if (sizeFilter) {
      const indicatorY = isExclusionMode ? 50 : 10;
      ctx.fillStyle = 'rgba(100, 100, 255, 0.9)';
      ctx.fillRect(10, indicatorY, 180, 25);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText(`SIZE: ${sizeFilter.min.toFixed(1)}-${sizeFilter.max.toFixed(1)} μm`, 18, indicatorY + 17);
    }
  }, [baseImage, filteredDetectionsWithIndex, result.image_size, classColors, excludedIndices, hoveredDetectionIndex, isExclusionMode, sizeFilter]);

  // Handle canvas click for exclusion
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isExclusionMode || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;

    // Find which detection was clicked (in reverse order for top-most first)
    for (let i = filteredDetectionsWithIndex.length - 1; i >= 0; i--) {
      const { det, originalIndex } = filteredDetectionsWithIndex[i];
      const { x1, y1, x2, y2 } = det.bbox;

      if (clickX >= x1 && clickX <= x2 && clickY >= y1 && clickY <= y2) {
        // Toggle exclusion
        setExcludedIndices(prev => {
          const newSet = new Set(prev);
          if (newSet.has(originalIndex)) {
            newSet.delete(originalIndex);
          } else {
            newSet.add(originalIndex);
          }
          return newSet;
        });
        break;
      }
    }
  };

  // Handle canvas mouse move for hover effect
  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isExclusionMode || !canvasRef.current) {
      setHoveredDetectionIndex(null);
      return;
    }

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    // Find hovered detection
    let foundIndex: number | null = null;
    for (let i = filteredDetectionsWithIndex.length - 1; i >= 0; i--) {
      const { det, originalIndex } = filteredDetectionsWithIndex[i];
      const { x1, y1, x2, y2 } = det.bbox;

      if (mouseX >= x1 && mouseX <= x2 && mouseY >= y1 && mouseY <= y2) {
        foundIndex = originalIndex;
        break;
      }
    }
    setHoveredDetectionIndex(foundIndex);
  };

  const handleCanvasMouseLeave = () => {
    setHoveredDetectionIndex(null);
  };

  // Reset exclusions and clear regenerated histograms
  const resetExclusions = () => {
    setExcludedIndices(new Set());
    setAppliedExcludedIndices(new Set()); // Also reset applied state
    setRegeneratedHistograms({});
    // Notify parent to clear exclusions for this file and regenerate batch histograms
    if (onResetExclusions) {
      onResetExclusions();
    }
  };

  // Notify parent when exclusions change
  React.useEffect(() => {
    if (onExclusionsChange) {
      onExclusionsChange(excludedIndices, result.results.detections);
    }
  }, [excludedIndices, onExclusionsChange, result.results.detections]);

  const toggleClass = (className: string) => {
    setSelectedClasses((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(className)) {
        // Allow deselecting to show raw image
        newSet.delete(className);
      } else {
        newSet.add(className);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedClasses(new Set(Object.keys(result.results.class_counts)));
  };

  const deselectAll = () => {
    // Allow viewing raw image with no annotations
    setSelectedClasses(new Set());
  };

  const toggleExpanded = (className: string) => {
    setExpandedClasses((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(className)) {
        newSet.delete(className);
      } else {
        newSet.add(className);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (!statsByClass) return;
    const allClasses = Object.keys(result.results.class_counts).filter(
      className => selectedClasses.has(className) && statsByClass[className]?.count > 0
    );
    setExpandedClasses(new Set(allClasses));
  };

  const collapseAll = () => {
    setExpandedClasses(new Set());
  };

  return (
    <div ref={ref} className="border dark:border-gray-800 rounded-lg p-6 transition-shadow duration-300 hover:shadow-xl bg-card">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold">{filename}</h4>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="font-mono">
            {activeCount} detections{activeCount !== result.results.total_detections ? ` (of ${result.results.total_detections})` : ''}
          </Badge>
          <span className="text-sm text-muted-foreground font-mono">
            {result.image_size.width} x {result.image_size.height} px
          </span>
        </div>
      </div>

      <Separator className="mb-4" />

      {/* Exclusion Mode Controls */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant={isExclusionMode ? "destructive" : "outline"}
            size="sm"
            onClick={() => setIsExclusionMode(!isExclusionMode)}
          >
            {isExclusionMode ? (
              <>
                <X className="w-4 h-4 mr-1" />
                Exit Exclusion Mode
              </>
            ) : (
              <>
                <MousePointer2 className="w-4 h-4 mr-1" />
                Exclude Detections
              </>
            )}
          </Button>
          {excludedCount > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetExclusions}
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset ({excludedCount})
              </Button>
              {onApplyExclusions && hasUnappliedChanges && (
                <Button
                  variant="default"
                  size="sm"
                  disabled={isRegeneratingHistograms}
                  onClick={async () => {
                    const activeDetectionsList = activeDetections.map(({ det }) => det);
                    onApplyExclusions(excludedIndices, activeDetectionsList);
                    // Regenerate histograms for affected classes
                    await regenerateAffectedHistograms();
                    // Mark these exclusions as applied
                    setAppliedExcludedIndices(new Set(excludedIndices));
                    setIsExclusionMode(false);
                  }}
                >
                  {isRegeneratingHistograms ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-1" />
                  )}
                  {isRegeneratingHistograms ? 'Updating...' : 'Apply Exclusions'}
                </Button>
              )}
            </>
          )}
        </div>
        {isExclusionMode && (
          <p className="text-sm text-muted-foreground">
            Click on detections to exclude them from analysis
          </p>
        )}
      </div>

      {/* Full-width Canvas Image */}
      {result.base_image && (
        <div className="mb-6">
          <canvas
            ref={canvasRef}
            className={`w-full rounded-lg border shadow-sm bg-black ${isExclusionMode ? 'cursor-crosshair' : ''}`}
            style={{ imageRendering: 'pixelated' }}
            onClick={handleCanvasClick}
            onMouseMove={handleCanvasMouseMove}
            onMouseLeave={handleCanvasMouseLeave}
          />
        </div>
      )}

      {/* Detection Summary and Analysis stacked below */}
      <div className="space-y-4">
          {/* Detection Summary - shows filtered count */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label>
                Detection Summary {selectedClasses.size === 0 ? '(Raw Image)' : (sizeFilter && excludedCount > 0) ? '(Size Filtered + Exclusions)' : sizeFilter ? '(Size Filtered)' : excludedCount > 0 ? '(Exclusions Applied)' : filteredCount !== result.results.total_detections ? '(Filtered)' : ''}
              </Label>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedClasses.size === Object.keys(result.results.class_counts).length}
                >
                  Select All
                </button>
                <span className="text-xs text-muted-foreground">|</span>
                <button
                  onClick={deselectAll}
                  className="text-xs text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedClasses.size === 0}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="text-center py-4">
              {selectedClasses.size === 0 ? (
                <>
                  <span className="text-2xl font-bold text-muted-foreground">Raw Image</span>
                  <p className="text-sm text-muted-foreground mt-2">No annotations visible - select classes to view detections</p>
                </>
              ) : activeCount === 0 ? (
                <>
                  <span className="text-2xl font-bold text-amber-600">0</span>
                  <p className="text-sm text-amber-600 mt-2">No detections match the current filters</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {sizeFilteredOutCount > 0 && `${sizeFilteredOutCount} outside size range`}
                    {sizeFilteredOutCount > 0 && excludedCount > 0 && ' • '}
                    {excludedCount > 0 && `${excludedCount} excluded`}
                  </p>
                </>
              ) : (
                <>
                  <span className="text-4xl font-bold font-mono">{activeCount}</span>
                  <span className="text-lg text-muted-foreground ml-2">
                    {sizeFilteredOutCount > 0 && excludedCount > 0 ? (
                      <>active detections <span className="text-blue-500">({sizeFilteredOutCount} size filtered)</span> <span className="text-red-500">({excludedCount} excluded)</span></>
                    ) : sizeFilteredOutCount > 0 ? (
                      <>active detections <span className="text-blue-500">({sizeFilteredOutCount} outside size range)</span></>
                    ) : excludedCount > 0 ? (
                      <>active detections <span className="text-red-500">({excludedCount} excluded)</span></>
                    ) : filteredCount !== result.results.total_detections ? (
                      `of ${result.results.total_detections} detections`
                    ) : (
                      'detections'
                    )}
                  </span>
                </>
              )}
            </div>
            {/* Visual Progress Bar */}
            {selectedClasses.size > 0 && activeCount > 0 && (
              <div className="mb-4">
                <div className="flex h-3 rounded-full overflow-hidden bg-muted/30">
                  {Object.entries(result.results.class_counts).map(([className]) => {
                    if (!selectedClasses.has(className)) return null;
                    const activeClassCount = activeCountsByClass[className] || 0;
                    if (activeClassCount === 0) return null;
                    const percentage = (activeClassCount / activeCount) * 100;
                    return (
                      <div
                        key={className}
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: classColors[className] || '#888'
                        }}
                        className="transition-all duration-300 hover:opacity-80"
                        title={`${className}: ${activeClassCount} (${percentage.toFixed(1)}%)`}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(result.results.class_counts).map(([className, originalCount]) => {
                const isSelected = selectedClasses.has(className);
                const activeClassCount = activeCountsByClass[className] || 0;
                const hasFiltering = (excludedCount > 0 || sizeFilter) && activeClassCount !== originalCount;
                return (
                  <div
                    key={className}
                    onClick={() => toggleClass(className)}
                    className={`flex items-center justify-between p-2 bg-background rounded-md border transition-all duration-200 cursor-pointer hover:scale-105 hover:shadow-md ${
                      isSelected ? 'opacity-100 shadow-sm' : 'opacity-40'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: classColors[className] || '#888' }}
                      />
                      <span className="flex-1 text-sm truncate">{formatClassName(className)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {hasFiltering && isSelected && (
                        <span className={`text-xs ${sizeFilter ? 'text-blue-500' : 'text-red-500'} line-through mr-1`}>{originalCount}</span>
                      )}
                      <Badge variant={classBadgeVariants[className] || 'outline'} className="font-mono">
                        {isSelected && (excludedCount > 0 || sizeFilter) ? activeClassCount : originalCount}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Per-Class Analysis - filtered */}
          {statsByClass && Object.keys(statsByClass).length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label>Analysis by Class</Label>
                {selectedClasses.size > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={expandAll}
                      className="text-xs text-primary hover:underline"
                    >
                      Expand All
                    </button>
                    <span className="text-xs text-muted-foreground">|</span>
                    <button
                      onClick={collapseAll}
                      className="text-xs text-primary hover:underline"
                    >
                      Collapse All
                    </button>
                  </div>
                )}
              </div>
              {selectedClasses.size === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Select detection classes above to view detailed analysis</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(statsByClass).map(([className, classStats]: [string, any]) => {
                    const isExpanded = expandedClasses.has(className);
                    // Use active stats when there are exclusions or size filter, otherwise use backend stats
                    const hasFiltering = excludedCount > 0 || sizeFilter;
                    const activeStats = activeStatsByClass[className];
                    // When filtering is active, use activeCountsByClass (defaults to 0 if class not present)
                    const activeClassCount = activeCountsByClass[className] || 0;
                    const displayCount = hasFiltering ? activeClassCount : classStats.count;
                    const displayMean = hasFiltering && activeStats ? activeStats.mean_um : classStats.mean_um;
                    const displayMedian = hasFiltering && activeStats ? activeStats.median_um : classStats.median_um;
                    const displayStd = hasFiltering && activeStats ? activeStats.std_um : classStats.std_um;
                    // Mode is not recalculated locally, use backend value
                    const displayMode = classStats.mode_um;

                    // Don't show classes with 0 active detections when filtering is active
                    if (hasFiltering && displayCount === 0) return null;

                    // Also skip if class has no original detections or is not selected
                    if (classStats.count === 0 || !selectedClasses.has(className)) return null;

                    return (
                        <div key={className} className="bg-background rounded-md border transition-all duration-200 hover:shadow-md">
                          {/* Class Header - Clickable */}
                          <div
                            className="flex items-center gap-2 p-4 cursor-pointer hover:bg-muted/50 transition-all duration-200 min-w-0 flex"
                            onClick={() => toggleExpanded(className)}
                          >
                            {/* Expand/Collapse Icon */}
                            <svg
                              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: classColors[className] || '#888' }}
                            />
                            <span className="text-sm font-semibold">{formatClassName(className)}</span>
                            <Badge variant="outline" className="ml-auto truncate">{displayCount} detections</Badge>
                          </div>

                          {/* Collapsible Content */}
                          {isExpanded && (
                            <div className="px-4 pb-4 space-y-3">

                      {/* Size Metrics */}
                      <div className="mb-3">
                        <p className="text-xs text-muted-foreground mb-2">Size Distribution{hasFiltering ? ' (recalculated)' : ''}:</p>
                        <div className="grid grid-cols-4 gap-2">
                          <div className="text-center bg-muted/30 rounded p-2">
                            <p className="text-xs text-muted-foreground">Mean</p>
                            <p className="text-sm font-bold font-mono">{displayMean?.toFixed(2)} μm</p>
                          </div>
                          <div className="text-center bg-muted/30 rounded p-2">
                            <p className="text-xs text-muted-foreground">Median</p>
                            <p className="text-sm font-bold font-mono">{displayMedian?.toFixed(2)} μm</p>
                          </div>
                          <div className="text-center bg-muted/30 rounded p-2">
                            <p className="text-xs text-muted-foreground">Mode</p>
                            <p className="text-sm font-bold font-mono">{displayMode?.toFixed(2)} μm</p>
                          </div>
                          <div className="text-center bg-muted/30 rounded p-2">
                            <p className="text-xs text-muted-foreground">Std Dev</p>
                            <p className="text-sm font-bold font-mono">{displayStd?.toFixed(2)} μm</p>
                          </div>
                        </div>
                      </div>

                      {/* Shape Metrics */}
                      {classStats.shape_metrics && (
                        <div className="border-t pt-3">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Shape Characteristics</p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                            <ShapeMetricBar
                              label="Roundness"
                              value={classStats.shape_metrics.roundness ?? 0}
                              color={classColors[className] || '#3b82f6'}
                            />
                            <ShapeMetricBar
                              label="Convexity"
                              value={classStats.shape_metrics.convexity ?? 0}
                              color={classColors[className] || '#3b82f6'}
                            />
                            <ShapeMetricBar
                              label="Aspect Ratio"
                              value={classStats.shape_metrics.aspect_ratio ?? 0}
                              maxValue={2}
                              color={classColors[className] || '#3b82f6'}
                            />
                            <ShapeMetricBar
                              label="Solidity"
                              value={classStats.shape_metrics.solidity ?? 0}
                              color={classColors[className] || '#3b82f6'}
                            />
                          </div>
                        </div>
                      )}

                              {/* Per-Class Histogram - use regenerated if available */}
                              {(() => {
                                const histogramToShow = regeneratedHistograms[className] || classStats.histogram;
                                const isRegenerated = !!regeneratedHistograms[className];
                                if (!histogramToShow) return null;
                                return (
                                  <div className="border-t pt-3 mt-3">
                                    <p className="text-xs text-muted-foreground mb-2">
                                      Size Distribution{isRegenerated ? ' (updated)' : ''}:
                                    </p>
                                    <img
                                      src={`data:image/png;base64,${histogramToShow}`}
                                      alt={`${className} size distribution`}
                                      className="w-full rounded-lg border"
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        {/* Image Info */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="outline" className="font-mono">Calibration: {pixelToUm} μm/px</Badge>
          <Badge variant="outline" className="font-mono">Confidence: {(confThreshold * 100).toFixed(0)}%</Badge>
        </div>
      </div>
    </div>
  );
});

DetectionResultCard.displayName = 'DetectionResultCard';

export default DetectionResultCard;
